import { ROUTE_META } from '@/app/routes/route-meta';
import { normalizePublicProfileUsername } from '@/data/contracts/publicCreatorProfile';

/**
 * Compatibility aliases that have one unambiguous canonical destination.
 * Keep this list intentionally small: aliases that need data resolution or
 * lossy query translation belong to their owning route instead.
 */
export const LEGACY_ROUTE_REDIRECTS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    ROUTE_META
      .filter(
        (route) =>
          route.disposition === 'redirect' &&
          !route.path.includes(':') &&
          Boolean(route.canonicalPath),
      )
      .map((route) => [route.path, route.canonicalPath as string]),
  ),
);

const DISPLAY_MAP_PATH = '/displays/map';

function resolveSequencerProfileAlias(pathname: string, search: string, hash: string) {
  const match = /^\/sequencer\/([^/]+)$/.exec(pathname);

  if (!match) {
    return null;
  }

  let rawUsername = match[1];

  try {
    rawUsername = decodeURIComponent(rawUsername);
  } catch {
    // Keep the original segment when an inbound URL has malformed encoding.
  }

  const username = normalizePublicProfileUsername(rawUsername);

  if (!username) {
    return null;
  }

  return appendLocationState(`/profile/${encodeURIComponent(username)}`, search, hash);
}

function appendLocationState(destination: string, search: string, hash: string) {
  const url = new URL(destination, 'https://light-hunt.invalid');

  // Keep compatibility defaults (for example `near=me` and `#cookies`) while
  // appending any state supplied by the legacy URL.
  new URLSearchParams(search).forEach((value, key) => {
    url.searchParams.append(key, value);
  });

  // An inbound fragment is more specific than a compatibility default.
  if (hash) {
    url.hash = hash;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

const SEQUENCE_TAXONOMY_KEYS = ['genre', 'holiday', 'software'] as const;
const KNOWN_SOFTWARE_SLUGS = new Set([
  'xlights',
  'light-o-rama',
  'lightorama',
  'lor',
  'vixen',
  'hls',
  'falcon-player',
]);

function normalizeTaxonomySlug(value: string) {
  const camelCaseSeparated = value.trim().replace(/([a-z0-9])([A-Z])/g, '$1-$2');
  return camelCaseSeparated
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Translate the old `/sequences?genre=…` style links into the canonical
 * taxonomy routes. Ambiguous or unsupported combinations stay in the search
 * surface so no query state is silently discarded.
 */
export function resolveSequenceQueryRoute(search = '', hash = ''): string | null {
  // Callers normally pass `search` and `hash` separately, but accepting a
  // combined value keeps this resolver safe for direct URL-string consumers.
  if (!hash) {
    const embeddedHashIndex = search.indexOf('#');
    if (embeddedHashIndex !== -1) {
      hash = search.slice(embeddedHashIndex);
      search = search.slice(0, embeddedHashIndex);
    }
  }

  const params = new URLSearchParams(search);
  const presentKeys = SEQUENCE_TAXONOMY_KEYS.filter((key) => params.has(key));

  if (presentKeys.length === 0) {
    return null;
  }

  const key = presentKeys[0];
  const values = params.getAll(key);
  const rawValue = values[0] ?? '';
  const normalized = normalizeTaxonomySlug(rawValue);
  const isSingleValue = presentKeys.length === 1 && values.length === 1;
  const hasCommaSeparatedValue = rawValue.includes(',');
  const isKnownSoftware =
    key !== 'software' ||
    KNOWN_SOFTWARE_SLUGS.has(normalized) ||
    KNOWN_SOFTWARE_SLUGS.has(normalized.replace(/-/g, ''));

  if (!isSingleValue || !normalized || hasCommaSeparatedValue || !isKnownSoftware) {
    return appendLocationState('/sequences/search', search, hash);
  }

  const canonicalSlug =
    key === 'software' && normalized.replace(/-/g, '') === 'xlights'
      ? 'xlights'
      : normalized;
  const remainingParams = new URLSearchParams(params);
  remainingParams.delete(key);
  const remainingSearch = remainingParams.toString();
  const canonicalPath =
    key === 'genre'
      ? `/sequences/genre/${canonicalSlug}`
      : key === 'holiday'
        ? `/sequences/holiday/${canonicalSlug}`
        : `/sequences/${canonicalSlug}`;

  return appendLocationState(
    canonicalPath,
    remainingSearch ? `?${remainingSearch}` : '',
    hash,
  );
}

/**
 * Resolve a legacy pathname to a canonical location, retaining URL state.
 * Returns null for canonical or intentionally unsupported paths so mounting
 * this component cannot redirect a route back into itself.
 */
export function resolveLegacyRoute(
  pathname: string,
  search = '',
  hash = ''
): string | null {
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  if (normalizedPathname === '/sequences') {
    return resolveSequenceQueryRoute(search, hash);
  }

  const exactDestination =
    LEGACY_ROUTE_REDIRECTS[normalizedPathname as keyof typeof LEGACY_ROUTE_REDIRECTS];

  if (exactDestination) {
    return appendLocationState(exactDestination, search, hash);
  }

  const sequencerProfileAlias = resolveSequencerProfileAlias(
    normalizedPathname,
    search,
    hash,
  );

  if (sequencerProfileAlias) {
    return sequencerProfileAlias;
  }

  const displayAlias = /^\/displays\/([^/]+)$/.exec(normalizedPathname);

  if (displayAlias && displayAlias[1] !== 'map') {
    return appendLocationState(`/display/${displayAlias[1]}`, search, hash);
  }

  // Keep the canonical map route out of the dynamic display alias branch.
  if (normalizedPathname === DISPLAY_MAP_PATH) {
    return null;
  }

  return null;
}
